import React from 'react';
import { ExternalLink, Linkedin, Globe } from 'lucide-react';

const AboutPage = () => {
  const founders = [
    {
      name: 'Sean Talluri',
      title: 'Worship Leader & Pastor',
      image: 'https://customer-assets.emergentagent.com/job_elbethel-worship/artifacts/u31qj9iw_Sean%20Profile%20pic.png',
      education: [
        'D.Min. in Expository Preaching and Teaching - Liberty University (2026)',
        'M.A. in Biblical Exposition - Liberty University (2024)',
        'M.S. in Technology Management - NJIT (2007)',
        'M.B.A. in Information Technology - ICFAI Business School (2003)'
      ],
      roles: [
        'Worship Leader & Pastor at El Bethel Church',
        'Director of Product at Upbound Group',
        'Professor of Product Management at Dallas Baptist University'
      ],
      bio: 'Sean was saved by God\'s grace at 18 and experienced deep spiritual transformation in Hyderabad, India (2004). He combines his passion for technology and ministry to serve God\'s kingdom through biblical teaching, fellowship, and discipleship. His motto from Philippians 4:13: "I can do all things through Christ who strengthens me."',
      links: [
        { type: 'website', url: 'https://seantalluri.com', label: 'Personal Website' },
        { type: 'linkedin', url: 'https://linkedin.com/in/seantalluri', label: 'LinkedIn Profile' }
      ]
    },
    {
      name: 'Rhoda Talluri',
      title: 'Sunday School & Women\'s Ministry Leader',
      image: 'https://customer-assets.emergentagent.com/job_elbethel-worship/artifacts/o620sqlb_20250922_184400_0000.png',
      education: [
        'DScPT - Andrews University (2020)'
      ],
      roles: [
        'Sunday School & Women\'s Ministry Leader at El Bethel Church',
        'Physical Therapist at UT Southwestern Medical Center'
      ],
      bio: 'Rhoda is a cornerstone in spiritual and community life, dedicated to nurturing members with love and care. She leads Sunday School and Women\'s Ministry with passion, while also serving patients as a Physical Therapist.',
      links: []
    }
  ];

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0">
      {/* Hero Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 lg:mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 font-display">
              About El Bethel Church
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed font-sans mb-6">
              A local church in Grand Prairie, Texas, committed to exalting Christ through expository 
              preaching and authentic worship. We gather weekly to follow the pattern of the New Testament 
              church found in Acts 2:42.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <a 
                href="/visit"
                className="bg-[#f17b21] hover:bg-[#d86919] text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-300"
              >
                Plan Your Visit
              </a>
              <a 
                href="/contact"
                className="border-2 border-[#f17b21] text-[#f17b21] hover:bg-[#f17b21] hover:text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-300"
              >
                Get In Touch
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8 font-display">
              Our Mission
            </h2>
            <div className="bg-gray-50 rounded-2xl p-8 lg:p-12">
              <p className="text-xl lg:text-2xl text-gray-700 font-light leading-relaxed font-sans mb-6">
                "To exalt Christ through expository preaching and authentic worship"
              </p>
              <div className="text-[#f17b21] font-semibold font-sans">
                — El Bethel Church Mission Statement
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Church & Ministries Relationship */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8 text-center font-display">
              Our Structure
            </h2>
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <p className="text-gray-700 leading-relaxed font-sans">
                <strong className="text-gray-800">El Bethel Church</strong> is our local congregation 
                in Grand Prairie, Texas, where believers gather for worship, teaching, fellowship, and prayer 
                following the Acts 2:42 pattern. Our broader ministry includes three interconnected organizations:
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-bold text-gray-800 mb-3 font-display">El Bethel Ministries</h3>
                <p className="text-gray-700 text-sm mb-3 font-sans">
                  Our broader ministry activities and outreach initiatives that extend beyond our Sunday 
                  gatherings, encompassing all our ministry work and digital presence including elbethel.online.
                </p>
                <a 
                  href="https://elbethel.online" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#f17b21] hover:text-[#d86919] font-semibold text-sm flex items-center"
                >
                  Visit Site <ExternalLink className="w-4 h-4 ml-1" />
                </a>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-bold text-gray-800 mb-3 font-display">The Blanket Project</h3>
                <p className="text-gray-700 text-sm mb-3 font-sans">
                  Our community outreach ministry serving underprivileged and underserved communities 
                  in the Dallas metro area through blankets, food, and essential supplies.
                </p>
                <a 
                  href="https://theblanket.us" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#f17b21] hover:text-[#d86919] font-semibold text-sm flex items-center"
                >
                  Learn More <ExternalLink className="w-4 h-4 ml-1" />
                </a>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-bold text-gray-800 mb-3 font-display">Christ Everywhere</h3>
                <p className="text-gray-700 text-sm mb-3 font-sans">
                  Our sister organization - a digital Christian community platform connecting believers 
                  at work, school, church, and home for prayer, fellowship, and spiritual growth.
                </p>
                <a 
                  href="https://christeverywhere.org" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#f17b21] hover:text-[#d86919] font-semibold text-sm flex items-center"
                >
                  Learn More <ExternalLink className="w-4 h-4 ml-1" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-6 font-display">
              Our Leaders
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto font-sans">
              Meet the dedicated leaders of El Bethel Church, committed to shepherding God's people 
              through faithful biblical teaching and Christ-centered worship.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
            {founders.map((founder, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-xl p-6 lg:p-8 hover:shadow-2xl transition-all duration-300">
                <div className="text-center mb-6">
                  <div className="w-24 h-24 lg:w-32 lg:h-32 mx-auto mb-6 rounded-full overflow-hidden shadow-lg">
                    <img 
                      src={founder.image}
                      alt={founder.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-2 font-display">{founder.name}</h3>
                  <p className="text-[#f17b21] font-semibold text-base lg:text-lg font-sans">{founder.title}</p>
                </div>

                <div className="space-y-6">
                  {/* Bio */}
                  <div>
                    <p className="text-gray-700 leading-relaxed text-sm lg:text-base font-sans">{founder.bio}</p>
                  </div>

                  {/* Education */}
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3 text-sm lg:text-base font-display">Education</h4>
                    <ul className="space-y-2">
                      {founder.education.map((edu, idx) => (
                        <li key={idx} className="text-gray-600 text-xs lg:text-sm flex items-start font-sans">
                          <span className="text-[#f17b21] mr-2">•</span>
                          <span>{edu}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Current Roles */}
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3 text-sm lg:text-base font-display">Current Roles</h4>
                    <ul className="space-y-2">
                      {founder.roles.map((role, idx) => (
                        <li key={idx} className="text-gray-600 text-xs lg:text-sm flex items-start font-sans">
                          <span className="text-[#f17b21] mr-2">•</span>
                          <span>{role}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Links */}
                  {founder.links.length > 0 && (
                    <div className="flex flex-wrap gap-3 pt-4">
                      {founder.links.map((link, idx) => (
                        <a
                          key={idx}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-2 bg-gray-100 hover:bg-[#f17b21] text-gray-700 hover:text-white rounded-lg transition-colors duration-300 text-xs lg:text-sm font-sans"
                        >
                          {link.type === 'linkedin' && <Linkedin className="w-4 h-4 mr-1.5" />}
                          {link.type === 'website' && <Globe className="w-4 h-4 mr-1.5" />}
                          {link.type === 'ministry' && <ExternalLink className="w-4 h-4 mr-1.5" />}
                          <span>{link.label}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;